import { createBrowserRouter, redirect, type RouteObject } from "react-router";
import { RouteErrorBoundary } from "@/components/misc/RouteErrorBoundary";
import { supabase } from "@/utils/supabase/client";

// Public landing stays at /. The product checks admission before loading viewer data.
const WaitlistPage = () => import("@/routes/waitlist");
const PrivacyPage = () => import("@/routes/privacy");
const TermsPage = () => import("@/routes/terms");

// Supabase can fall back to its configured site URL after OAuth. Complete that
// callback before rendering the waitlist, preserving the PKCE code and errors.
async function landingLoader({ request }: { request: Request }) {
  const url = new URL(request.url);
  if (url.searchParams.has("code") || url.searchParams.has("error")) {
    return redirect(`/auth/callback${url.search}`);
  }
  if (url.pathname === "/") {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) return redirect("/discover");
  }
  return null;
}

const routes: RouteObject[] = [
  { path: "/", loader: landingLoader, lazy: WaitlistPage, ErrorBoundary: RouteErrorBoundary },
  { path: "/waitlist", loader: landingLoader, lazy: WaitlistPage, ErrorBoundary: RouteErrorBoundary },
  {
    path: "/app",
    loader: ({ request }) => {
      const url = new URL(request.url);
      return redirect(`/discover${url.search}${url.hash}`);
    },
  },
  {
    path: "/watchlist",
    loader: ({ request }) => {
      const url = new URL(request.url);
      return redirect(`/saved${url.search}${url.hash}`);
    },
  },
  {
    path: "/privacy",
    lazy: PrivacyPage,
    ErrorBoundary: RouteErrorBoundary,
  },
  {
    path: "/terms",
    lazy: TermsPage,
    ErrorBoundary: RouteErrorBoundary,
  },
  ...["/account", "/discover", "/auth/callback", "/connect/:provider", "/saved", "/records/:id"].map(path => ({
    path,
    lazy: () => import("@/routes/product"),
    ErrorBoundary: RouteErrorBoundary,
  })),
  { path: "/list/:id", lazy: () => import("@/routes/shared-list"), ErrorBoundary: RouteErrorBoundary },
  { path: "*", loader: () => redirect("/") },
];

export const router = createBrowserRouter(routes);
